/**
 * Transform utility class for rotation and projection calculations
 * Centralizes 3D transform math used throughout the engine
 */
export default class Transform {
  /**
   * Get camera rotation parameters (sin and cos)
   * @param {Object} camera - Camera object with angle property
   * @return {Object} Object with cos and sin properties
   */
  static getCameraRotation(camera) {
    const rad = camera.angle * Math.PI / 180;
    return {
      cos: Math.cos(rad),
      sin: Math.sin(rad),
      rad: rad
    };
  }

  /**
   * Get rotation matrix values for an angle in degrees
   * @param {number} angleDegrees - Angle in degrees
   * @return {Object} Object with cos, sin, and rad properties
   */
  static getRotationMatrix(angleDegrees) {
    const rad = angleDegrees * Math.PI / 180;
    return {
      cos: Math.cos(rad),
      sin: Math.sin(rad),
      rad: rad
    };
  }

  /**
   * Rotate a point around the origin
   * @param {number} x - X coordinate
   * @param {number} z - Z coordinate
   * @param {number} cos - Cosine of rotation angle
   * @param {number} sin - Sine of rotation angle
   * @return {Object} Rotated coordinates {x, z}
   */
  static rotatePoint(x, z, cos, sin) {
    return {
      x: x * cos - z * sin,
      z: x * sin + z * cos
    };
  }

  /**
   * Rotate a point around a specific origin
   * @param {number} x - X coordinate
   * @param {number} z - Z coordinate
   * @param {number} originX - Origin X coordinate
   * @param {number} originZ - Origin Z coordinate
   * @param {number} cos - Cosine of rotation angle
   * @param {number} sin - Sine of rotation angle
   * @return {Object} Rotated coordinates {x, z}
   */
  static rotatePointAround(x, z, originX, originZ, cos, sin) {
    const relX = x - originX;
    const relZ = z - originZ;
    return {
      x: relX * cos - relZ * sin,
      z: relX * sin + relZ * cos
    };
  }

  /**
   * Calculate Y scale from camera skew angle
   * Maps skew angle to projection factor:
   * skewAngle = 0 → 0° pitch → yScale = 0 (pure side view)
   * skewAngle = ±1 → ±45° pitch → yScale ≈ ±0.707 (typical isometric)
   * skewAngle = ±2 → ±90° pitch → yScale = ±1 (true top/bottom view)
   *
   * @param {number} skewAngle - Camera skew angle
   * @return {number} Y scale factor
   */
  static calculateYScale(skewAngle) {
    const pitchRad = (skewAngle * Math.PI) / 4; // Map skewAngle to radians (±2 → ±π/2)
    return Math.sin(pitchRad);
  }

  /**
   * Calculate absolute Y scale (no negative values)
   * @param {number} skewAngle - Camera skew angle
   * @return {number} Absolute Y scale factor
   */
  static calculateAbsYScale(skewAngle) {
    return Math.abs(this.calculateYScale(skewAngle));
  }

  /**
   * Project 3D world coordinates to camera space
   * @param {number} worldX - World X coordinate
   * @param {number} worldZ - World Z coordinate
   * @param {Object} camera - Camera object with position, angle, and skewAngle
   * @param {number} cos - Cosine of camera angle (optional, computed if not provided)
   * @param {number} sin - Sine of camera angle (optional, computed if not provided)
   * @param {number} yScale - Y scale factor (optional, computed if not provided)
   * @return {Object} Camera space coordinates {x, z}
   */
  static worldToCameraSpace(worldX, worldZ, camera, cos = null, sin = null, yScale = null) {
    // Compute rotation if not provided
    if (cos === null || sin === null) {
      const rotation = this.getCameraRotation(camera);
      cos = rotation.cos;
      sin = rotation.sin;
    }

    // Compute yScale if not provided
    if (yScale === null) {
      yScale = this.calculateYScale(camera.skewAngle);
    }

    // Translate relative to camera position
    const relX = worldX - camera.position.x;
    const relZ = worldZ - camera.position.z;

    // Rotate and apply Y scale
    return {
      x: relX * cos - relZ * sin,
      z: (relX * sin + relZ * cos) * yScale
    };
  }

  /**
   * Project 3D world position to 2D screen position (in world-space, before CSS zoom)
   * For elements inside #world, use this directly (CSS scale on #world handles zoom).
   * For elements outside #world (e.g., in #camera), multiply result by camera.zoomLevel.
   * @param {number} worldX - World X coordinate
   * @param {number} worldY - World Y coordinate
   * @param {number} worldZ - World Z coordinate
   * @param {Object} camera - Camera object
   * @param {number} cameraPositionScale - Scale factor for camera position (default 1, use zoom on Android)
   * @return {Object} Screen coordinates {x, y} in world-space (before zoom)
   */
  static worldToScreen(worldX, worldY, worldZ, camera, cameraPositionScale = 1) {
    const rotation = this.getCameraRotation(camera);
    const yScale = this.calculateYScale(camera.skewAngle);

    // For Android baking: scale camera position to match zoomed world coordinates
    const scaledCamPosX = camera.position.x * cameraPositionScale;
    const scaledCamPosZ = camera.position.z * cameraPositionScale;

    // Translate relative to (possibly scaled) camera position
    const relX = worldX - scaledCamPosX;
    const relZ = worldZ - scaledCamPosZ;

    // Rotate and apply Y scale
    const cameraSpaceX = relX * rotation.cos - relZ * rotation.sin;
    const cameraSpaceZ = (relX * rotation.sin + relZ * rotation.cos) * yScale;

    // Apply camera.position.y to offset screen Y, keeping the followed target centered
    // Scale by cameraPositionScale for Android baking consistency
    const cameraY = (camera.position.y || 0) * cameraPositionScale;
    return {
      x: cameraSpaceX,
      y: cameraSpaceZ - worldY + cameraY
    };
  }

  /**
   * Convert screen coordinates back to world coordinates (inverse projection)
   * Useful for mouse picking and interaction
   * @param {number} screenX - Screen X coordinate
   * @param {number} screenY - Screen Y coordinate
   * @param {Object} camera - Camera object
   * @param {number} worldY - Known world Y coordinate (defaults to 0)
   * @return {Object} World coordinates {x, z}
   */
  static screenToWorld(screenX, screenY, camera, worldY = 0) {
    const rotation = this.getCameraRotation(camera);
    const yScale = this.calculateYScale(camera.skewAngle);

    // Reverse zoom and camera Y offset
    const cameraY = camera.position.y || 0;
    const cameraX = screenX / camera.zoomLevel;
    const cameraZ = screenY / camera.zoomLevel + worldY - cameraY;

    // Reverse Y scale
    const adjustedZ = yScale !== 0 ? cameraZ / yScale : 0;

    // Reverse rotation
    const worldX = cameraX * rotation.cos + adjustedZ * rotation.sin + camera.position.x;
    const worldZ = -cameraX * rotation.sin + adjustedZ * rotation.cos + camera.position.z;

    return { x: worldX, z: worldZ };
  }

  /**
   * Calculate isometric projection for a point
   * @param {number} x - X coordinate
   * @param {number} y - Y coordinate
   * @param {number} z - Z coordinate
   * @param {number} angle - Camera angle in degrees
   * @param {number} yScale - Y scale factor
   * @return {Object} Projected coordinates {x, y}
   */
  static calculateIsometricProjection(x, y, z, angle, yScale) {
    const rotation = this.getRotationMatrix(angle);
    const rotatedX = x * rotation.cos - z * rotation.sin;
    const rotatedZ = (x * rotation.sin + z * rotation.cos) * yScale;

    return {
      x: rotatedX,
      y: rotatedZ - y
    };
  }
}
